import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance, { API_URL } from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import showErrorToast, {
  showSuccessToast,
  showWarningToast,
} from "../../components/CustomComponents/toast/toast";
import "./promotionBanners.scss";
import "./onlineOrderingBanners.scss";

const bannerImageUrl = (filePath) => {
  if (!filePath) return "";
  const base = (API_URL || "").replace(/\/$/, "");
  const path = String(filePath).replace(/^\//, "");
  return `${base}/${path}`;
};

const MAX_ACTIVE_OO_BANNERS = 10;
const MAX_BANNER_FILE_BYTES = 10 * 1024 * 1024;
const MAX_BANNER_FILE_LABEL = "10 MB";

const ACCEPT_IMAGE = /^image\/(jpeg|png|gif|webp)$/i;
const ACCEPT_EXT = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

const getBannerId = (b) =>
  b?.onlineOrderingBanner_ID ??
  b?.onlineOrderingBannerId ??
  b?.OnlineOrderingBanner_ID ??
  null;

const filterImageFiles = (fileList) => {
  const list = fileList ? Array.from(fileList) : [];
  return list.filter((f) => {
    if (!f) return false;
    if (ACCEPT_IMAGE.test(f.type || "")) return true;
    const ext = (f.name || "").split(".").pop()?.toLowerCase();
    return ext ? ACCEPT_EXT.has(ext) : false;
  });
};

const formatUploadErrorMessage = (payload) => {
  const errors = payload?.data?.errors ?? payload?.Data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .map((err) => {
        const name = err?.fileName || err?.FileName || "File";
        const msg = err?.message || err?.Message || "Upload failed";
        return `${msg} (${name})`;
      })
      .join(" · ");
  }
  return payload?.message || payload?.Message || "Upload failed";
};

const sortBannersByOrder = (list) => {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const sa = Number(a.sortOrder) || 0;
    const sb = Number(b.sortOrder) || 0;
    if (sa !== sb) return sa - sb;
    return (getBannerId(a) || 0) - (getBannerId(b) || 0);
  });
};

const OnlineOrderingBanners = () => {
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [savingMetaId, setSavingMetaId] = useState(null);
  const [banners, setBanners] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [dropzoneActive, setDropzoneActive] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const sortedBanners = useMemo(() => sortBannersByOrder(banners), [banners]);
  const enabledCount = banners.filter((b) => b.isEnabled).length;

  const syncDraftsFromBanners = useCallback((rows) => {
    const next = {};
    (rows || []).forEach((b) => {
      const id = getBannerId(b);
      if (id == null) return;
      next[id] = {
        title: b.title ?? b.Title ?? "",
        linkUrl: b.linkUrl ?? b.LinkUrl ?? "",
      };
    });
    setDrafts(next);
  }, []);

  const loadBanners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ONLINE_ORDERING_BANNERS);
      if (response.data?.status) {
        const rows = response.data?.data;
        const list = Array.isArray(rows) ? rows : [];
        setBanners(list);
        syncDraftsFromBanners(list);
      } else {
        showErrorToast(response.data?.message || "Failed to load banners");
        setBanners([]);
        setDrafts({});
      }
    } catch (e) {
      showErrorToast("Failed to load Online Ordering banners");
      setBanners([]);
      setDrafts({});
    } finally {
      setLoading(false);
    }
  }, [syncDraftsFromBanners]);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const uploadFiles = useCallback(
    async (files) => {
      const images = filterImageFiles(files);
      if (images.length === 0) {
        showErrorToast("Add one or more image files (JPG, PNG, GIF, or WebP).");
        return;
      }

      const tooLarge = images.filter((f) => (f.size || 0) > MAX_BANNER_FILE_BYTES);
      const withinLimit = images.filter((f) => (f.size || 0) <= MAX_BANNER_FILE_BYTES);

      if (tooLarge.length > 0) {
        showErrorToast(
          tooLarge
            .map((f) => `File exceeds maximum size (${MAX_BANNER_FILE_LABEL}) (${f.name})`)
            .join(" · ")
        );
      }
      if (withinLimit.length === 0) return;

      if (tooLarge.length > 0) {
        showWarningToast(
          `Skipped ${tooLarge.length} file(s) over ${MAX_BANNER_FILE_LABEL}. Uploading the rest…`
        );
      }

      const formData = new FormData();
      withinLimit.forEach((f) => formData.append("files", f));
      try {
        setUploading(true);
        const response = await axiosInstance.post(
          APIs.POST_ONLINE_ORDERING_BANNERS_UPLOAD,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        const payload = response.data || {};
        const errors = payload?.data?.errors ?? payload?.Data?.errors;
        const hasErrors = Array.isArray(errors) && errors.length > 0;

        if (payload.status) {
          showSuccessToast(payload.message || payload.Message || "Upload completed");
          if (hasErrors) showWarningToast(formatUploadErrorMessage(payload));
          await loadBanners();
        } else {
          showErrorToast(formatUploadErrorMessage(payload));
        }
      } catch (err) {
        showErrorToast(formatUploadErrorMessage(err.response?.data || {}));
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [loadBanners]
  );

  const onFileInputChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    uploadFiles(files);
  };

  const openFilePicker = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  const onDropzoneDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDropzoneActive(true);
  };

  const onDropzoneDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDropzoneActive(false);
    }
  };

  const onDropzoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = "copy";
    } catch {
      /* ignore */
    }
  };

  const onDropzoneDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDropzoneActive(false);
    if (uploading) return;
    uploadFiles(e.dataTransfer?.files);
  };

  const patchBanner = async (bannerId, body) => {
    try {
      const response = await axiosInstance.patch(APIs.PATCH_ONLINE_ORDERING_BANNER, {
        onlineOrderingBanner_ID: bannerId,
        ...body,
      });
      if (response.data?.status) {
        showSuccessToast(response.data?.message || "Saved");
        await loadBanners();
        return true;
      }
      showErrorToast(response.data?.message || "Update failed");
      return false;
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.Message ||
        "Update failed";
      showErrorToast(msg);
      return false;
    }
  };

  const persistGalleryOrder = async (ordered) => {
    const toUpdate = [];
    ordered.forEach((b, i) => {
      const newSortOrder = (i + 1) * 10;
      if (Number(b.sortOrder) !== newSortOrder) {
        toUpdate.push({
          id: getBannerId(b),
          sortOrder: newSortOrder,
        });
      }
    });
    if (toUpdate.length === 0) return;

    try {
      setReordering(true);
      for (const u of toUpdate) {
        const response = await axiosInstance.patch(APIs.PATCH_ONLINE_ORDERING_BANNER, {
          onlineOrderingBanner_ID: u.id,
          sortOrder: u.sortOrder,
        });
        if (!response.data?.status) {
          showErrorToast(response.data?.message || "Could not save order");
          await loadBanners();
          return;
        }
      }
      showSuccessToast("Gallery order saved");
      await loadBanners();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ||
          err.response?.data?.Message ||
          "Could not save order"
      );
      await loadBanners();
    } finally {
      setReordering(false);
    }
  };

  const handleReorderDrop = async (e, dropBannerId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    if (reordering || uploading) return;

    const raw = e.dataTransfer?.getData("text/plain");
    const dragId = parseInt(raw, 10);
    if (!dragId || dragId === dropBannerId || Number.isNaN(dragId)) return;

    const list = sortBannersByOrder(banners);
    const fromIdx = list.findIndex((x) => getBannerId(x) === dragId);
    const toIdx = list.findIndex((x) => getBannerId(x) === dropBannerId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;

    const next = [...list];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    await persistGalleryOrder(next);
  };

  const onToggleEnabled = async (banner, checked) => {
    const id = getBannerId(banner);
    if (checked && !banner.isEnabled && enabledCount >= MAX_ACTIVE_OO_BANNERS) {
      showErrorToast(
        `You can enable at most ${MAX_ACTIVE_OO_BANNERS} banners. Disable another banner first.`
      );
      return;
    }
    await patchBanner(id, { isEnabled: checked });
  };

  const onDraftChange = (bannerId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [bannerId]: {
        title: prev[bannerId]?.title ?? "",
        linkUrl: prev[bannerId]?.linkUrl ?? "",
        [field]: value,
      },
    }));
  };

  const onSaveMeta = async (banner) => {
    const id = getBannerId(banner);
    if (id == null) return;
    const draft = drafts[id] || { title: "", linkUrl: "" };
    const title = String(draft.title ?? "").trim();
    const linkUrl = String(draft.linkUrl ?? "").trim();

    if (linkUrl) {
      const isRelative = linkUrl.startsWith("/");
      const isHttp = /^https?:\/\//i.test(linkUrl);
      if (!isRelative && !isHttp) {
        showErrorToast(
          "Link URL must be a relative path (e.g. /menu) or an http/https URL"
        );
        return;
      }
    }

    try {
      setSavingMetaId(id);
      await patchBanner(id, { title, linkUrl });
    } finally {
      setSavingMetaId(null);
    }
  };

  const onDelete = async (banner) => {
    const bannerId = getBannerId(banner);
    if (bannerId == null || Number.isNaN(Number(bannerId))) {
      showErrorToast("Invalid banner id");
      return;
    }
    if (!window.confirm("Delete this Online Ordering banner? This cannot be undone.")) {
      return;
    }
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_ONLINE_ORDERING_BANNER(Number(bannerId))
      );
      if (response.data?.status) {
        showSuccessToast(response.data?.message || "Deleted");
        await loadBanners();
      } else {
        showErrorToast(response.data?.message || "Delete failed");
      }
    } catch (e) {
      showErrorToast("Delete failed");
    }
  };

  const onCardDragStart = (e, bannerId) => {
    if (reordering || uploading) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(bannerId));
    try {
      e.dataTransfer.setDragImage(
        e.currentTarget.closest(".promotion-banners-card") || e.currentTarget,
        0,
        0
      );
    } catch {
      /* optional */
    }
    setDraggingId(bannerId);
  };

  const onCardDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const onCardDragOver = (e, bannerId) => {
    if (!draggingId || draggingId === bannerId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(bannerId);
  };

  const onCardDragLeave = (e, bannerId) => {
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    if (dragOverId === bannerId) setDragOverId(null);
  };

  if (loading) {
    return <GridSectionLoader />;
  }

  return (
    <div className="promotion-banners-page online-ordering-banners-page container-fluid p-4">
      <div className="promotion-banners-header mb-3">
        <div>
          <h4 className="mb-1">Online Ordering Banners</h4>
          <p className="text-muted mb-0 small">
            Promo slides for the Online Ordering carousel (wired later). Set an optional
            title and link URL per image. Drag{" "}
            <i className="fa-solid fa-grip-vertical mx-1" aria-hidden="true" /> to reorder.
            Up to <strong>{MAX_ACTIVE_OO_BANNERS}</strong> can be active; new uploads start
            inactive.
          </p>
        </div>
        <div className="promotion-banners-enabled-pill">
          Active <strong>{enabledCount}</strong> / {MAX_ACTIVE_OO_BANNERS}
        </div>
      </div>

      <div
        className={`promotion-banners-dropzone ${dropzoneActive ? "is-active" : ""} ${uploading ? "is-busy" : ""}`}
        onDragEnter={onDropzoneDragEnter}
        onDragLeave={onDropzoneDragLeave}
        onDragOver={onDropzoneDragOver}
        onDrop={onDropzoneDrop}
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openFilePicker();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Upload Online Ordering banner images. Maximum ${MAX_BANNER_FILE_LABEL} per file.`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          className="promotion-banners-dropzone-input"
          onChange={onFileInputChange}
          onClick={(e) => e.stopPropagation()}
        />
        <div className="promotion-banners-dropzone-inner">
          <div className="promotion-banners-dropzone-icon" aria-hidden="true">
            <i className="fa-solid fa-cloud-arrow-up" />
          </div>
          <p className="promotion-banners-dropzone-title mb-1">
            {uploading ? "Uploading…" : "Drop images here or click to browse"}
          </p>
          <p className="promotion-banners-dropzone-hint text-muted small mb-0">
            JPG, PNG, GIF, WebP · multiple files · max{" "}
            <strong>{MAX_BANNER_FILE_LABEL}</strong> per image
          </p>
        </div>
      </div>

      <p className="promotion-banners-size-note text-muted small mt-2 mb-0" role="note">
        <i className="fa-solid fa-circle-info me-1" aria-hidden="true" />
        Link URLs: relative paths like <code>/menu</code> or full{" "}
        <code>https://…</code> links. Leave blank for image-only.
      </p>

      {reordering && (
        <p className="text-muted small mt-2 mb-0">Saving gallery order…</p>
      )}

      <div
        className={`promotion-banners-gallery mt-4 ${reordering ? "is-reordering" : ""}`}
      >
        {sortedBanners.length === 0 ? (
          <div className="promotion-banners-empty">
            <i className="fa-regular fa-images promotion-banners-empty-icon" />
            <p className="mb-0 text-muted">
              No banners yet. Use the dropzone above to add images.
            </p>
          </div>
        ) : (
          sortedBanners.map((b) => {
            const id = getBannerId(b);
            const draft = drafts[id] || { title: "", linkUrl: "" };
            return (
              <article
                key={id}
                className={`promotion-banners-card ${b.isEnabled ? "is-enabled" : ""} ${draggingId === id ? "is-dragging" : ""} ${dragOverId === id ? "is-drag-over" : ""}`}
                onDragOver={(e) => onCardDragOver(e, id)}
                onDragLeave={(e) => onCardDragLeave(e, id)}
                onDrop={(e) => {
                  void handleReorderDrop(e, id);
                }}
              >
                <div className="promotion-banners-card-image-wrap">
                  <img
                    src={bannerImageUrl(b.filePath)}
                    alt=""
                    className="promotion-banners-card-image"
                    loading="lazy"
                    draggable={false}
                  />
                  <span
                    className={`promotion-banners-card-status badge ${b.isEnabled ? "text-bg-success" : "text-bg-secondary"}`}
                  >
                    {b.isEnabled ? "Active" : "Inactive"}
                  </span>
                  <div
                    className="promotion-banners-card-drag-handle"
                    draggable={!reordering && !uploading}
                    onDragStart={(e) => onCardDragStart(e, id)}
                    onDragEnd={onCardDragEnd}
                    onClick={(e) => e.stopPropagation()}
                    role="button"
                    tabIndex={0}
                    aria-label="Drag to reorder gallery"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") e.preventDefault();
                    }}
                  >
                    <i className="fa-solid fa-grip-vertical" aria-hidden="true" />
                  </div>
                </div>
                <div className="promotion-banners-card-body">
                  <div className="oo-banner-fields">
                    <label className="oo-banner-field-label" htmlFor={`oo-title-${id}`}>
                      Title
                    </label>
                    <input
                      id={`oo-title-${id}`}
                      type="text"
                      className="form-control form-control-sm"
                      maxLength={200}
                      placeholder="Optional label"
                      value={draft.title}
                      onChange={(e) => onDraftChange(id, "title", e.target.value)}
                      disabled={savingMetaId === id}
                    />
                    <label className="oo-banner-field-label" htmlFor={`oo-link-${id}`}>
                      Link URL
                    </label>
                    <input
                      id={`oo-link-${id}`}
                      type="text"
                      className="form-control form-control-sm"
                      maxLength={1000}
                      placeholder="/menu or https://…"
                      value={draft.linkUrl}
                      onChange={(e) => onDraftChange(id, "linkUrl", e.target.value)}
                      disabled={savingMetaId === id}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary w-100 mt-2"
                      onClick={() => onSaveMeta(b)}
                      disabled={savingMetaId === id || reordering || uploading}
                    >
                      {savingMetaId === id ? "Saving…" : "Save title & link"}
                    </button>
                  </div>
                  <div className="promotion-banners-card-toggle mt-2">
                    <ToggleSwitch
                      isChecked={!!b.isEnabled}
                      onToggle={(checked) => onToggleEnabled(b, checked)}
                      label="Show on Online Ordering"
                      name={`enabled-${id}`}
                      disabled={!b.isEnabled && enabledCount >= MAX_ACTIVE_OO_BANNERS}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger w-100 mt-2"
                    onClick={() => onDelete(b)}
                  >
                    <i className="fa-regular fa-trash-can me-1" aria-hidden="true" />
                    Delete
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
};

export default OnlineOrderingBanners;
